import React, {useState, FC, ChangeEvent, useEffect} from 'react'
import {useFormik, FormikHelpers} from 'formik'
import * as Yup from 'yup'

// Import React Bootstrap components
import {Form, Button, Container, Row, Col, Spinner, InputGroup, Alert} from 'react-bootstrap'
import {
  useSetupAccountFromInviteMutation,
  useVerifyInviteTokenMutation,
} from '../../../../../services/cellcast'
import {isEmpty, isPlainObject} from 'lodash'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import { fetchAsyncVerifyOtp, UserLoginAuth } from './UserLoginRedux/UserLoginSlice'
import { useDispatch } from 'react-redux'
import RegistrationOtpModal from './RegistartionOtpModal'
import { getUserData } from './UserLoginRedux/UserLoginSlice'
import { useSelector } from 'react-redux'
import { resetRedux } from '../../../../Redux/store'

// --- Type Definitions ---
interface FormValues {
  avatar?: File | null
  firstName: string
  lastName: string
  jobTitle?: string
  mobile: string
  password: string
  passwordConfirmation: string
  email: string
}

// --- Status View Component ---
// This component will now handle loading, success, and error states for link verification.
const VerifyLink: FC<{isLoading: boolean; message?: string; isError: boolean}> = ({
  isLoading,
  message,
  isError,
}) => (
  <Container style={{maxWidth: '600px'}}>
    <div className='card shadow-sm border-0 rounded-4 p-4 p-sm-5 text-center'>
      <div className='card-body'>
        {isLoading && (
          <>
            <Spinner
              animation='border'
              variant='primary'
              className='mb-3'
              style={{width: '3rem', height: '3rem'}}
            />
            <h1 className='fw-bold mb-3'>Verifying Link</h1>
            <p className='text-muted mb-4'>
              {message || 'Please wait while we check your invitation...'}
            </p>
          </>
        )}
        {isError && (
          <>
            <i className='bi bi-x-circle-fill display-1 text-danger mb-3'></i>
            <h1 className='fw-bold mb-3'>Link Invalid</h1>
            <p className='text-muted mb-4'>
              {message ||
                'This invitation link is no longer valid. Please request a new invitation.'}
            </p>
          </>
        )}
      </div>
    </div>
  </Container>
)

// --- Main Form Component ---
export const InviteLink: FC = () => {
  const dispatch = useDispatch()
  const [verificationStatus, setVerificationStatus] = useState<{
    isLoading: boolean
    isValid: boolean
    error: string
  }>({
    isLoading: true,
    isValid: false,
    error: '',
  })
  const [token, setToken] = useState<string | null>(null)
  const user = useSelector(getUserData)

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [submitStatus, setSubmitStatus] = useState<{type?: 'success' | 'danger'; message?: string}>(
    {}
  )
  const [otpVisible, setOtpVisible] = useState(false)
  const [OTP, setOTP] = useState<any>()

  const [verifyInviteToken] = useVerifyInviteTokenMutation()
  const [setupAccountFromInvite] = useSetupAccountFromInviteMutation()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const tokenFromUrl = urlParams.get('token')

    if (tokenFromUrl) {
      setToken(tokenFromUrl)
      verifyInviteToken({token: tokenFromUrl})
        .then((res: any) => {
          if (isPlainObject(res?.error) && !isEmpty(res.error)) {
            return setVerificationStatus({
              isLoading: false,
              isValid: false,
              error: res?.error?.message,
            })
          }
          setVerificationStatus({isLoading: false, isValid: true, error: ''})
          const userData = res?.data?.data

          formik.setFieldValue('email', userData?.email)
          formik.setFieldValue('firstName', userData?.firstName)
          formik.setFieldValue('lastName', userData?.lastName)
          formik.setFieldValue('jobTitle', userData?.jobTitle)
          formik.setFieldValue('mobile', userData?.mobile)
        })
        .catch((err) => {
          setVerificationStatus({isLoading: false, isValid: false, error: err.message})
        })
    } else {
      setVerificationStatus({
        isLoading: false,
        isValid: false,
        error: 'No invitation token found in the link.',
      })
    }
  }, [])

  useEffect(() => {
    if (user?.otp) {
      setOtpVisible(true)
    }
  }, [user])

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().max(25, 'Too long').required('First name is required').trim(),
    lastName: Yup.string().max(25, 'Too long').required('Last name is required').trim(),
    mobile: Yup.string()
      .matches(/^(?:\+?61|0)?[45]\d{8}$/, 'Invalid mobile number')
      .required('Mobile is required'),
    jobTitle: Yup.string().trim(),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
        'Password is too weak'
      )
      .max(50)
      .required('Password is required'),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
      .required('Confirm password is required'),
    avatar: Yup.mixed().optional(),
  })

  const formik = useFormik<FormValues>({
    initialValues: {
      avatar: null,
      firstName: '',
      lastName: '',
      mobile: '',
      jobTitle: '',
      password: '',
      passwordConfirmation: '',
      email: '',
    },
    validationSchema,
    onSubmit: (values: FormValues, {setSubmitting}: FormikHelpers<FormValues>) => {
      setSubmitStatus({})
      const formData = new FormData()
      ;(Object.keys(values) as Array<keyof FormValues>).forEach((key) => {
        const value = values[key]
        if (key === 'avatar') {
          if (value instanceof File) {
            formData.append(key, value)
          }
        } else if (value != null) {
          formData.append(key, String(value))
        }
      })
      if (token) {
        formData.append('token', token)
      }

      console.log('Submitting FormData...')
      formData.forEach((value, key) => {
        console.log(`${key}:`, value)
      })

      setupAccountFromInvite(formData)
        .then((res: any) => {
          if (isPlainObject(res?.error) && !isEmpty(res.error)) {
            return setSubmitStatus({
              type: 'danger',
              message: res.error.message,
            })
          }
          setSubmitStatus({
            type: 'success',
            message: 'Your account has been set up successfully!',
          })

          let data = {
            email: values.email,
            password: values.password,
          }
          dispatch(resetRedux())
          dispatch(UserLoginAuth(data))
          toast.success('Your account has been set up successfully! Please login')
        })
        .catch((err) => {
          setSubmitStatus({type: 'danger', message: err.message})
        })

      setTimeout(() => {
        setSubmitStatus({type: 'success', message: 'Your account has been set up successfully!'})
        setSubmitting(false)
      }, 2000)
    },
  })

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    if (file) {
      formik.setFieldValue('avatar', file)
      setAvatarPreview(URL.createObjectURL(file))
    }
  }

  const handleEnter: any = (event: any) => {
    const data = {
      otp: OTP,
      mobile: user?.mobile,
      email: user?.email,
      type: user?.otpType || 'SIGNUP_MOBILE',
    }

    dispatch(fetchAsyncVerifyOtp(data)).then((res: any) => {
      if (!res.error) {
        setOtpVisible(false)
      }
    })
  }

  if (verificationStatus.isLoading || !verificationStatus.isValid) {
    return (
      <VerifyLink
        isLoading={verificationStatus.isLoading}
        isError={!verificationStatus.isValid && !verificationStatus.isLoading}
        message={verificationStatus.error}
      />
    )
  }

  return (
    <>
      {user?.otp && (
        <RegistrationOtpModal
          show={otpVisible}
          handleClose={() => setOtpVisible(false)}
          email={user?.email}
          phoneNumber={user?.mobile}
          stopLoading={undefined}
          handleEnter={() => handleEnter()}
          setOTP={setOTP}
          OTP={OTP}
          otpType={user?.otpType}
        />
      )}
      <div className='bg-light d-flex align-items-center justify-content-center min-vh-100 py-5'>
        <Container style={{maxWidth: '800px'}}>
          <div className='card shadow-sm border-0 rounded-4 p-4 p-sm-5'>
            <div className='card-body'>
              {/* <div className='text-center mb-4'>
                <img
                  src={toAbsoluteUrl('/media/logos/LogoSymbol.svg')}
                  alt='Cellcast logo'
                  style={{maxWidth: '100px', height: 'auto'}}
                />
              </div> */}
              <h1 className='text-center fw-bold mb-3'>Welcome to Cellcast!</h1>
              <h2 className='text-center fw-bold mb-3'>Please Complete Your Account Setup</h2>
              <p className='text-center text-muted mb-4'>
                Welcome! Please fill in your details to secure your account.
              </p>

              <Form noValidate onSubmit={formik.handleSubmit}>
                <Form.Group className='text-center mb-4'>
                  <Form.Label htmlFor='avatar-upload' style={{cursor: 'pointer'}}>
                    <div className='image-input image-input-outline w-auto'>
                      <img
                        src={avatarPreview || toAbsoluteUrl('/media/avatars/blank.png')}
                        alt='Avatar Preview'
                        // className='rounded-circle'
                        style={{
                          width: '128x',
                          height: '128px',
                          objectFit: 'cover',
                          border: '3px solid #dee2e6',
                        }}
                      />
                      <div
                        className='position-absolute bottom-0 end-0 bg-primary rounded-circle d-flex align-items-center justify-content-center'
                        style={{width: '32px', height: '32px', border: '2px solid white'}}
                      >
                        <i className='bi bi-pencil-fill text-white fs-6'></i>
                      </div>
                    </div>
                  </Form.Label>
                  <Form.Control
                    type='file'
                    id='avatar-upload'
                    accept='image/*'
                    onChange={handleAvatarChange}
                    style={{display: 'none'}}
                  />
                </Form.Group>

                <Row className='g-4'>
                  <Col md={6}>
                    <Form.Group controlId='firstName'>
                      <Form.Label>First Name</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type='text'
                          placeholder='e.g., Jane'
                          {...formik.getFieldProps('firstName')}
                          isInvalid={formik.touched.firstName && !!formik.errors.firstName}
                          isValid={formik.touched.firstName && !formik.errors.firstName}
                        />
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.firstName}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId='lastName'>
                      <Form.Label>Last Name</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type='text'
                          placeholder='e.g., Doe'
                          {...formik.getFieldProps('lastName')}
                          isInvalid={formik.touched.lastName && !!formik.errors.lastName}
                          isValid={formik.touched.lastName && !formik.errors.lastName}
                        />
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.lastName}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId='mobile'>
                      <Form.Label>Phone Number</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type='tel'
                          placeholder='e.g., 0412 345 678'
                          {...formik.getFieldProps('mobile')}
                          isInvalid={formik.touched.mobile && !!formik.errors.mobile}
                          isValid={formik.touched.mobile && !formik.errors.mobile}
                        />
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.mobile}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId='mobile'>
                      <Form.Label>Job Title</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type='text'
                          placeholder='e.g., Sales'
                          {...formik.getFieldProps('jobTitle')}
                          isInvalid={formik.touched.jobTitle && !!formik.errors.jobTitle}
                          isValid={formik.touched.jobTitle && !formik.errors.jobTitle}
                        />
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.jobTitle}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId='password'>
                      <Form.Label>Password</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          placeholder='Enter new password'
                          {...formik.getFieldProps('password')}
                          isInvalid={formik.touched.password && !!formik.errors.password}
                          isValid={formik.touched.password && !formik.errors.password}
                        />
                        <Button
                          variant='primary'
                          onClick={() => setShowPassword(!showPassword)}
                          className='d-flex align-items-center btn btn-icon'
                        >
                          <i className={`${showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} fs-5`}></i>
                        </Button>
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.password}
                        </Form.Control.Feedback>
                      </InputGroup>
                      <Form.Text muted>
                        Use 8+ characters with a mix of letters, numbers & symbols.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId='passwordConfirmation'>
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup hasValidation>
                        <Form.Control
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder='Confirm new password'
                          {...formik.getFieldProps('passwordConfirmation')}
                          isInvalid={
                            formik.touched.passwordConfirmation &&
                            !!formik.errors.passwordConfirmation
                          }
                          isValid={
                            formik.touched.passwordConfirmation &&
                            !formik.errors.passwordConfirmation &&
                            !!formik.values.passwordConfirmation
                          }
                        />
                        <Button
                          variant='primary'
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className='d-flex align-items-center btn btn-icon'
                        >
                          <i
                            className={`${
                              showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'
                            } fs-5`}
                          ></i>
                        </Button>
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.passwordConfirmation}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                {submitStatus.message && (
                  <Alert variant={submitStatus.type} className='mt-4'>
                    {submitStatus.message}
                  </Alert>
                )}

                <div className='d-grid mt-4'>
                  <Button type='submit' variant='primary' size='lg' disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className='me-2'
                        />
                        Processing...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
          <p className='text-center text-muted small mt-4'>
            By signing up, you agree to our{' '}
            <a
              href='https://www.cellcast.com/au/legal/terms-conditions'
              target='_blank'
              rel='noreferrer'
            >
              Terms of Service
            </a>
            .
          </p>
        </Container>
      </div>
    </>
  )
}

export default InviteLink
